import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  type LinkTelemetryBucketViewModel,
  type LinkTelemetryMetricViewModel,
  type LinkTelemetryUnitGroupViewModel,
} from '../linkDetailTelemetry';
import { formatValue } from './helpers';

export type LinkTelemetryChartProps = {
  unitGroup: LinkTelemetryUnitGroupViewModel;
  bucket: LinkTelemetryBucketViewModel;
};

const STROKE_COLORS = {
  a: {
    rx: 'var(--color-info)',
    tx: 'var(--color-success)',
    none: 'var(--color-info)',
  },
  z: {
    rx: 'var(--color-secondary)',
    tx: 'var(--color-warning)',
    none: 'var(--color-secondary)',
  },
};

const getMetricStroke = (metric: LinkTelemetryMetricViewModel) => {
  return STROKE_COLORS[metric.endpoint][metric.direction] ?? STROKE_COLORS.a.none;
};

const sanitizeId = (id: string) => id.replace(/[^a-zA-Z0-9]/g, '-');

const stateLabel = (state: string) =>
  ({
    available: 'Available',
    stale: 'Stale',
    partial: 'Partial data',
    failed: 'Collection failed',
    unsupported: 'Unsupported',
    unavailable: 'Unavailable',
    no_samples_in_range: 'No samples in range',
    disabled: 'Collection disabled',
  })[state] ?? state.replace(/_/g, ' ');

const metricMetadata = (metric: LinkTelemetryMetricViewModel) => {
  const metadata: string[] = [];
  if (metric.role) metadata.push(`Role: ${metric.role.replace(/_/g, ' ')}`);
  if (metric.health) metadata.push(`Health: ${metric.health}`);
  metadata.push(`State: ${stateLabel(metric.state)}`);
  if (metric.observedAt) metadata.push(`Observed: ${new Date(metric.observedAt).toLocaleString()}`);
  const thresholds = [
    metric.warningMin != null ? `warning low ${formatValue(metric.warningMin, metric.unit)}` : '',
    metric.warningMax != null ? `warning high ${formatValue(metric.warningMax, metric.unit)}` : '',
    metric.criticalMin != null ? `critical low ${formatValue(metric.criticalMin, metric.unit)}` : '',
    metric.criticalMax != null ? `critical high ${formatValue(metric.criticalMax, metric.unit)}` : '',
  ].filter(Boolean);
  if (thresholds.length) metadata.push(`Thresholds: ${thresholds.join(', ')}`);
  return metadata;
};

const StateDisplay = ({
  title,
  message,
  accentColor,
}: {
  title: string;
  message?: string;
  accentColor?: string;
}) => (
  <div
    className="flex h-full items-center justify-center rounded-lg border border-border/35 bg-card/25 px-4 text-center text-[10px] text-muted-foreground/80"
    style={{ borderLeft: accentColor ? `2px solid ${accentColor}` : undefined }}
  >
    <div>
      <p className="font-semibold" style={{ color: accentColor }}>
        {title}
      </p>
      {message && <p className="mt-1 text-[10px] text-muted-foreground/70">{message}</p>}
    </div>
  </div>
);

const ChartLegend = ({ metrics, unit }: { metrics: LinkTelemetryMetricViewModel[]; unit: string }) => (
  <ul
    className="mt-2 grid list-none grid-cols-1 gap-x-3 gap-y-2 text-[10px] sm:grid-cols-2"
    aria-label="Chart series legend"
  >
    {metrics.map(metric => (
      <li key={metric.metricId} className="flex items-start gap-2">
        <div
          className="mt-1 h-2 w-2 flex-shrink-0 rounded-full"
          style={{ backgroundColor: getMetricStroke(metric) }}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate">
              <span className="font-semibold text-foreground">
                {metric.endpoint.toUpperCase()} {metric.direction.toUpperCase()}:
              </span>
              <span className="ml-1 text-muted-foreground">
                {metric.label} {metric.lane ? `(Lane ${metric.lane})` : ''}
              </span>
            </span>
            <span className="shrink-0 font-mono text-[11px] font-medium text-foreground">
              {metric.currentValue != null ? formatValue(metric.currentValue, unit) : '—'}
            </span>
          </div>
          <div className="mt-0.5 text-[9px] leading-snug text-muted-foreground/70">
            {metricMetadata(metric).join(' · ')}
          </div>
        </div>
      </li>
    ))}
  </ul>
);

export const LinkTelemetryChart = ({ unitGroup, bucket }: LinkTelemetryChartProps) => {
  const { unit, metrics } = unitGroup;

  if (bucket.state !== 'available' && bucket.state !== 'stale' && bucket.state !== 'partial') {
    const diagnostic = bucket.endpoints.a.diagnostic || bucket.endpoints.z.diagnostic;
    return (
      <div className="h-40">
        <StateDisplay title={stateLabel(bucket.state)} message={diagnostic} />
      </div>
    );
  }

  const allPoints = new Map<number, Record<string, number | null>>();
  metrics.forEach(metric => {
    metric.points.forEach(point => {
      if (!allPoints.has(point.ts)) {
        allPoints.set(point.ts, { time: point.ts * 1000 }); // convert to ms for recharts
      }
      allPoints.get(point.ts)![metric.metricId] = point.value;
    });
  });

  const data = Array.from(allPoints.values()).sort((a, b) => a.time - b.time);

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-foreground/50">{unit}</div>
        <div className="flex gap-2">
          {bucket.endpoints.a.state !== 'available' && (
            <StateDisplay
              title={`A end: ${stateLabel(bucket.endpoints.a.state)}`}
              message={bucket.endpoints.a.diagnostic}
              accentColor="var(--color-info)"
            />
          )}
          {bucket.endpoints.z.state !== 'available' && (
            <StateDisplay
              title={`Z end: ${stateLabel(bucket.endpoints.z.state)}`}
              message={bucket.endpoints.z.diagnostic}
              accentColor="var(--color-warning)"
            />
          )}
        </div>
      </div>

      <div
        className="h-40 w-full"
        role="img"
        aria-label={`${bucket.category} telemetry chart in ${unit || 'unitless values'} with ${metrics.length} series`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <defs>
              {metrics.map(m => (
                <linearGradient key={m.metricId} id={`grad-${sanitizeId(m.metricId)}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={getMetricStroke(m)} stopOpacity={0.3} />
                  <stop offset="90%" stopColor={getMetricStroke(m)} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="var(--border-secondary)" strokeDasharray="3 3" strokeOpacity={0.5} />
            <XAxis
              dataKey="time"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={value => new Date(value).toLocaleTimeString()}
              tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
              axisLine={{ stroke: 'var(--border-secondary)' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={value => formatValue(value, unit, 2)}
              tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={70}
              domain={['auto', 'auto']}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-secondary)',
                fontSize: '12px',
              }}
              labelFormatter={label => new Date(label).toLocaleString()}
              formatter={(value, name, props) => [formatValue(value as number, unit), props.name]}
            />
            {metrics.map(metric => (
              <Area
                key={metric.metricId}
                type="monotone"
                dataKey={metric.metricId}
                name={`${metric.endpoint.toUpperCase()} ${metric.direction.toUpperCase()}: ${metric.label}`}
                stroke={getMetricStroke(metric)}
                strokeWidth={1.5}
                fill={`url(#grad-${sanitizeId(metric.metricId)})`}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <ChartLegend metrics={metrics} unit={unit} />
      <details className="mt-2 text-[10px] text-muted-foreground">
        <summary className="min-h-11 cursor-pointer py-3 font-semibold text-foreground">
          View chart data
        </summary>
        <div className="max-h-52 overflow-auto rounded border border-border/35">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">
              Current values and collection state for {bucket.category} telemetry
            </caption>
            <thead>
              <tr className="border-b border-border/35">
                <th className="px-2 py-2">Series</th>
                <th className="px-2 py-2">Current</th>
                <th className="px-2 py-2">State</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map(metric => (
                <tr key={metric.metricId} className="border-b border-border/20 last:border-0">
                  <td className="px-2 py-2">
                    {metric.endpoint.toUpperCase()} {metric.direction.toUpperCase()} {metric.label}
                  </td>
                  <td className="px-2 py-2">
                    {metric.currentValue != null ? formatValue(metric.currentValue, unit) : '—'}
                  </td>
                  <td className="px-2 py-2">{stateLabel(metric.state)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};
